/*
  cart-drawer-drag-to-close.js — pull the mobile bottom sheet down by its header to dismiss it.

  Pointer Events, not touch events: ekster listens to touchstart only and HIDES its
  handle on non-touch devices; our pill is always visible, so a mouse (desktop or a
  narrow emulated window) must be able to drag it too. Pointer events unify both.

  DELEGATED, not element-bound: every cart mutation makes Shopify re-render the
  drawer's contents, replacing the header node wholesale. Listeners bound to the
  header at load time die with it (drag worked until the first add-to-cart, then
  went dead - invisible in the editor where nothing gets added before testing).
  So pointerdown listens on document and resolves the CURRENT header per gesture;
  move/up handlers attach to that header for the gesture's lifetime only.

  The sheet's open/close animation belongs to Shrine's <cart-drawer> (remote main.js):
  `.active` drives `.drawer__inner` between translateY(100%) and translateY(0). This
  module only drives the inline transform DURING a drag, then hands control back:
  release past the threshold (or a fast downward flick) -> drawer.close() with the
  slide-out continuing from the pointer's position; otherwise -> spring back.
  Presses that start on the close X are left alone so its click never gets captured.
*/
(function () {
  'use strict';

  var isMobile = window.matchMedia('(max-width: 749px)');
  var state = null; /* one gesture at a time */

  function clearInline(inner) {
    inner.style.transition = '';
    inner.style.transform = '';
  }

  function finishOnce(inner) {
    var done = false;
    var fin = function () {
      if (done) return;
      done = true;
      clearInline(inner);
    };
    inner.addEventListener('transitionend', fin, { once: true });
    setTimeout(fin, 400); /* transitionend can be swallowed by visibility swaps */
  }

  function onMove(e) {
    if (!state || e.pointerId !== state.pointerId) return;
    state.delta = e.clientY - state.startY;
    if (state.delta > 0) {
      e.preventDefault(); /* the sheet follows the pointer; the page must not scroll */
      state.inner.style.transform = 'translateY(' + state.delta + 'px)';
    } else {
      state.inner.style.transform = 'translateY(0)'; /* no overdrag upward */
    }
  }

  function onRelease(e) {
    if (!state || e.pointerId !== state.pointerId) return;
    var s = state;
    state = null;
    s.header.removeEventListener('pointermove', onMove);
    s.header.removeEventListener('pointerup', onRelease);
    s.header.removeEventListener('pointercancel', onRelease);
    s.drawer.classList.remove('dragging');

    var elapsed = Math.max(performance.now() - s.startTime, 1);
    var velocity = s.delta / elapsed; /* px per ms, downward positive */
    var threshold = s.inner.getBoundingClientRect().height * 0.25;
    var shouldClose = s.delta > threshold || (s.delta > 48 && velocity > 0.5);

    if (shouldClose && typeof s.drawer.close === 'function') {
      /* continue the motion downward from where the pointer left off */
      s.inner.style.transition = 'transform 0.25s ease-in';
      s.inner.style.transform = 'translateY(100%)';
      /* unlock the page behind the still-moving sheet, mirroring the search fix */
      s.drawer.close();
      finishOnce(s.inner);
    } else {
      s.inner.style.transition = 'transform 0.3s ease-out';
      s.inner.style.transform = 'translateY(0)';
      finishOnce(s.inner);
    }
  }

  document.addEventListener('pointerdown', function (e) {
    /* a stale gesture whose header was re-rendered away can never release; drop it,
       and clear the inline transform off the (usually surviving) panel so the sheet
       cannot stay frozen mid-pull after a cart re-render landed during a drag */
    if (state && !state.header.isConnected) {
      state.drawer.classList.remove('dragging');
      if (state.inner && state.inner.isConnected) clearInline(state.inner);
      state = null;
    }
    if (state) return;
    if (!isMobile.matches || e.button !== 0) return;
    if (!(e.target instanceof Element)) return;
    var header = e.target.closest('cart-drawer .drawer__header, cart-drawer [data-cart-drag-zone]');
    if (!header || e.target.closest('.drawer__close')) return;
    var drawer = header.closest('cart-drawer');
    var inner = header.closest('.drawer__inner');
    if (!drawer || !inner || !drawer.classList.contains('active')) return;

    state = {
      drawer: drawer,
      inner: inner,
      header: header,
      startY: e.clientY,
      startTime: performance.now(),
      delta: 0,
      pointerId: e.pointerId,
    };
    drawer.classList.add('dragging');
    inner.style.transition = 'none';
    header.addEventListener('pointermove', onMove);
    header.addEventListener('pointerup', onRelease);
    header.addEventListener('pointercancel', onRelease);
    try { header.setPointerCapture(e.pointerId); } catch (err) { /* capture is best-effort */ }
  });
})();
/* cache-bust 2026-07-23 */
